class MuuvMarketplaceNavigation extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.clearAutoplay();
    this.abortController = new AbortController();

    const { signal } = this.abortController;
    this.audienceTabs = Array.from(this.querySelectorAll('[data-audience-tab]'));
    this.audiencePanels = Array.from(this.querySelectorAll('[data-audience-panel]'));
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.userPaused ??= this.motionQuery.matches;
    this.pointerHover = false;

    for (const tab of this.audienceTabs) {
      tab.addEventListener('click', () => this.activateAudience(tab.dataset.audience), { signal });
      tab.addEventListener('keydown', (event) => this.handleAudienceKeydown(event), { signal });
    }

    for (const button of this.querySelectorAll('[data-carousel-previous]')) {
      button.addEventListener('click', () => this.goTo(this.currentIndex - 1, true), { signal });
    }
    for (const button of this.querySelectorAll('[data-carousel-next]')) {
      button.addEventListener('click', () => this.goTo(this.currentIndex + 1, true), { signal });
    }
    for (const button of this.querySelectorAll('[data-carousel-toggle]')) {
      // Pointer focus stops rotation before click. Preserve the action requested
      // on pointerdown so a shopper clicking Pause cannot accidentally resume it.
      button.addEventListener('pointerdown', () => { this.pointerToggleIntent = !this.userPaused; }, { signal });
      button.addEventListener('pointercancel', () => { this.pointerToggleIntent = undefined; }, { signal });
      button.addEventListener('click', (event) => this.toggleAutoplay(event.detail > 0), { signal });
    }

    this.setActiveCarousel(this.audiencePanels.find((panel) => !panel.hidden));
    this.currentIndex = 0;

    for (const track of this.querySelectorAll('[data-carousel-track]')) {
      track.addEventListener('scroll', () => {
        if (track === this.track) this.updateIndexFromScroll();
      }, { signal, passive: true });
    }
    const stage = this.querySelector('.muuv-marketplace__carousel-stage');
    stage?.addEventListener('mouseenter', () => {
      this.pointerHover = true;
      this.pauseAutoplay();
    }, { signal });
    stage?.addEventListener('mouseleave', () => {
      this.pointerHover = false;
      this.resumeAutoplay();
    }, { signal });
    stage?.addEventListener('focusin', () => this.pauseAutoplay(true), { signal });
    this.track?.addEventListener('pointerdown', () => this.pauseAutoplay(true), { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseAutoplay(true);
    }, { signal });
    this.motionQuery.addEventListener('change', () => {
      if (this.motionQuery.matches) this.pauseAutoplay(true);
      this.refreshAutoplay();
    }, { signal });
    this.mobileQuery.addEventListener('change', () => {
      if (this.mobileQuery.matches) this.pauseAutoplay(true);
      this.refreshAutoplay();
    }, { signal });

    this.updateStatus(false);
    this.refreshAutoplay();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.clearAutoplay();
    window.cancelAnimationFrame(this.scrollFrame);
  }

  activateAudience(audience) {
    const targetPanel = this.audiencePanels.find((panel) => panel.dataset.audiencePanel === audience);
    const targetTab = this.audienceTabs.find((tab) => tab.dataset.audienceTab === audience);
    if (!targetPanel || !targetTab) return;

    for (const panel of this.audiencePanels) panel.hidden = panel !== targetPanel;
    for (const tab of this.audienceTabs) {
      const isActive = tab === targetTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    }

    this.setActiveCarousel(targetPanel);
    this.currentIndex = 0;
    this.track?.scrollTo({ left: 0, behavior: 'auto' });
    this.updateStatus(false);
    this.refreshAutoplay();
  }

  handleAudienceKeydown(event) {
    const enabledTabs = this.audienceTabs.filter((tab) => !tab.disabled);
    const current = enabledTabs.indexOf(event.currentTarget);
    if (current === -1) return;

    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % enabledTabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + enabledTabs.length) % enabledTabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = enabledTabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    enabledTabs[next].focus();
    this.activateAudience(enabledTabs[next].dataset.audienceTab);
  }

  setActiveCarousel(panel) {
    this.track = panel?.querySelector('[data-carousel-track]');
    this.slides = this.track ? Array.from(this.track.querySelectorAll('[data-carousel-slide]')) : [];
    this.status = panel?.querySelector('[data-carousel-status]');
    this.toggleButton = panel?.querySelector('[data-carousel-toggle]');
  }

  goTo(index, userInitiated = false) {
    if (!this.track || this.slides.length < 2) return;
    const normalized = (index + this.slides.length) % this.slides.length;
    const target = this.slides[normalized];
    if (!target) return;

    this.currentIndex = normalized;
    // Scroll only the track: scrollIntoView can also move the page vertically.
    const left = this.track.scrollLeft + target.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollTo({ left, behavior: this.motionQuery.matches ? 'auto' : 'smooth' });
    this.updateStatus(userInitiated);
    if (userInitiated) this.pauseAutoplay(true);
  }

  updateIndexFromScroll() {
    if (!this.track || this.slides.length === 0 || this.mobileQuery.matches) return;
    window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(() => {
      const trackLeft = this.track.getBoundingClientRect().left;
      const distances = this.slides.map((slide) => Math.abs(slide.getBoundingClientRect().left - trackLeft));
      const nextIndex = distances.indexOf(Math.min(...distances));
      if (nextIndex >= 0 && nextIndex !== this.currentIndex) {
        this.currentIndex = nextIndex;
        this.updateStatus(false);
      }
    });
  }

  updateStatus(userInitiated) {
    for (const [index, slide] of this.slides.entries()) {
      const inactive = index !== this.currentIndex;
      slide.inert = inactive;
      if (inactive) slide.setAttribute('aria-hidden', 'true');
      else slide.removeAttribute('aria-hidden');
    }
    if (!this.status) return;
    const total = String(this.slides.length).padStart(2, '0');
    const current = String(this.currentIndex + 1).padStart(2, '0');
    this.status.textContent = `${current} / ${total}`;
    this.status.setAttribute('aria-live', userInitiated ? 'polite' : 'off');
  }

  get autoplayEnabled() {
    return this.closest('[data-autoplay]')?.dataset.autoplay === 'true'
      && this.slides.length > 1
      && !this.motionQuery.matches
      && !this.mobileQuery.matches
      && !this.userPaused
      && !this.pointerHover
      && !document.hidden;
  }

  refreshAutoplay() {
    this.clearAutoplay();
    this.updateToggle();
    if (this.autoplayEnabled) this.scheduleAutoplay();
  }

  scheduleAutoplay() {
    if (!this.autoplayEnabled) return;
    const delay = Number(this.closest('[data-autoplay-delay]')?.dataset.autoplayDelay || 8000);
    this.autoplayTimer = window.setTimeout(() => {
      if (!this.autoplayEnabled) return;
      this.goTo(this.currentIndex + 1, false);
      this.scheduleAutoplay();
    }, delay);
  }

  clearAutoplay() {
    if (this.autoplayTimer) window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = undefined;
  }

  pauseAutoplay(persist = false) {
    if (persist) {
      this.userPaused = true;
    }
    this.clearAutoplay();
    this.updateToggle();
  }

  resumeAutoplay() {
    if (!this.userPaused) this.refreshAutoplay();
  }

  updateToggle() {
    if (!this.toggleButton) return;
    const motionBlocked = this.motionQuery.matches;
    const paused = this.userPaused || motionBlocked;
    this.toggleButton.setAttribute('aria-pressed', String(paused));
    this.toggleButton.setAttribute('aria-disabled', String(motionBlocked));
    this.toggleButton.textContent = motionBlocked ? 'Rotation off: reduced motion' : paused ? 'Resume rotation' : 'Pause rotation';
  }

  toggleAutoplay(fromPointer = false) {
    if (this.motionQuery.matches) return;
    this.userPaused = fromPointer && typeof this.pointerToggleIntent === 'boolean' ? this.pointerToggleIntent : !this.userPaused;
    this.pointerToggleIntent = undefined;
    this.refreshAutoplay();
  }
}

if (!customElements.get('muuv-marketplace-navigation')) {
  customElements.define('muuv-marketplace-navigation', MuuvMarketplaceNavigation);
}
